// A subscription to a product's updates, as the API names it
// (docs/features/product-updates.md).

export enum ProductUpdateEvent {
  BackInStock = 'back_in_stock',
  Discount = 'discount',
  NewVariant = 'new_variant',
}

// Once: each event is told the first time it happens, then dropped.
// Always: told every time, once a day at most; needs an account.
export enum ProductUpdateMode {
  Once = 'once',
  Always = 'always',
}

// In the order the switches show them.
export const ALL_PRODUCT_UPDATE_EVENTS: readonly ProductUpdateEvent[] = [
  ProductUpdateEvent.BackInStock,
  ProductUpdateEvent.NewVariant,
  ProductUpdateEvent.Discount,
];

// A row as the manage page and the account show it. `sku` absent: the card
// as a whole.
export interface ProductUpdateSubscriptionView {
  productId: string;
  slug: string;
  name: string;
  image: string;
  sku?: string;
  amountLabel?: string;
  events: ProductUpdateEvent[];
  mode: ProductUpdateMode;
  notifiedAt?: string;
}

export interface ProductUpdateSubscribeInput {
  // A guest's address; a signed-in customer's comes from the account.
  email?: string;
  sku?: string;
  events: ProductUpdateEvent[];
  mode?: ProductUpdateMode;
}

export interface ProductUpdateChangeInput {
  productId: string;
  sku?: string;
  // Empty: stop.
  events: ProductUpdateEvent[];
  mode?: ProductUpdateMode;
}

// The key of one subscription: product and amount (empty for the card).
export function productUpdateKey(productId: string, sku?: string): string {
  return `${productId}:${sku ?? ''}`;
}

// The row of one scope: the amount, or the card when `sku` is absent.
export function sameScope(row: Pick<ProductUpdateSubscriptionView, 'sku'>, sku?: string): boolean {
  return (row.sku ?? undefined) === (sku ?? undefined);
}

// An amount has no amounts of its own: a row about one is not offered the
// new-amount event.
export function eventsOfferedFor(sku?: string): ProductUpdateEvent[] {
  return ALL_PRODUCT_UPDATE_EVENTS.filter((event) => !(sku && event === ProductUpdateEvent.NewVariant));
}
